// Marketplace Phase 1 — Demand Insights i18n keys (Feb 2026).
//
// Merged into TRANSLATIONS[lang].marketplace.demand by translations.js.
// Two fully-translated source languages (EN/ES). The other 11 active
// languages and AR/HE fall back to EN — this is deliberate for Phase 1
// (admin-only surface, EN is the lingua franca of TEAM staff). When
// the surface graduates beyond admin in Phase 2/3 we'll fan out the
// remaining locales.

const pick = (m, lang) => m[lang] ?? m.en;

const _DEMAND = (lang) => ({
  // Sidebar nav label.
  navLabel: pick({
    en: "Demand Insights",
    es: "Insights de demanda",
    ca: "Informació de Demanda",
    eu: "Eskariaren Informazioa",
    gl: "Información da Demanda",
    ar: "رؤى الطلب",
    he: "תובנות ביקוש",
    it: "Informazioni sulla domanda",
    fr: "Aperçus de la demande",
    "pt-br": "Insights de Demanda",
    ja: "需要インサイト",
    hi: "मांग अंतर्दृष्टि",
    ko: "수요 인사이트",
    ru: "Аналитика спроса",
    cs: "Přehledy poptávky",
    nl: "Vraaginzichten",
    zh: "需求洞察",
    de: "Nachfrage-Einblicke",
  }, lang),
  // Page chrome.
  pageTitle: pick({
    en: "TEAM Connect · Demand Insights",
    es: "TEAM Connect · Insights de demanda",
    ca: "TEAM Connect · Informació de Demanda",
    eu: "TEAM Connect · Eskariaren Informazioa",
    gl: "TEAM Connect · Información da Demanda",
    ar: "TEAM Connect · رؤى الطلب",
    he: "TEAM Connect · תובנות ביקוש",
    it: "TEAM Connect · Informazioni sulla domanda",
    fr: "TEAM Connect · Aperçus de la demande",
    "pt-br": "TEAM Connect · Insights de Demanda",
    ja: "TEAM Connect · 需要インサイト",
    hi: "TEAM Connect · मांग अंतर्दृष्टि",
    ko: "TEAM Connect · 수요 인사이트",
    ru: "TEAM Connect · Аналитика спроса",
    cs: "TEAM Connect · Přehledy poptávky",
    nl: "TEAM Connect · Vraaginzichten",
    zh: "TEAM Connect · 需求洞察",
    de: "TEAM Connect · Nachfrage-Einblicke",
  }, lang),
  pageSub: pick({
    en: "Aggregated, k-anonymous view of player demand. No user IDs · no PII · minors excluded.",
    es: "Vista agregada y k-anónima de la demanda de jugadores. Sin IDs · sin PII · menores excluidos.",
    ca: "Vista agregada i k-anònima de la demanda dels jugadors. Sense IDs d'usuari · sense dades personals · menors exclosos.",
    eu: "Agregatutako ikuspegi k-anonimoa jokalarien eskariaz. Erabiltzaile IDrik ez · DPIrik ez · adingabekoak baztertuta.",
    gl: "Vista agregada e k-anónima da demanda de xogadores. Sen IDs de usuario · sen PII · menores excluídos.",
    ar: "عرض مُجمَّع ومجهول الهوية لطلبات اللاعبين. لا معرّفات مستخدمين · لا معلومات شخصية · القُصّر مستبعدون.",
    he: "תצוגה מצרפית ואנונימית (K-anonymity) של ביקוש שחקנים. ללא מזהי משתמש · ללא מידע אישי · קטינים לא נכללים.",
    it: "Vista aggregata e k-anonima della domanda dei giocatori. Nessun ID utente · nessun dato personale · minori esclusi.",
    fr: "Vue agrégée et k-anonyme de la demande des joueurs. Aucun ID utilisateur · aucune DCP · mineurs exclus.",
    "pt-br": "Visão agregada e k-anônima da demanda de jogadores. Sem IDs de usuário · sem PII · menores excluídos.",
    ja: "集約されたk-匿名化されたプレイヤー需要のビュー。ユーザーID不使用 · 個人情報不使用 · 未成年者除外。",
    hi: "समेकित, k-गुमनाम खिलाड़ी मांग दृश्य। कोई उपयोगकर्ता ID नहीं · कोई PII नहीं · नाबालिग बाहर।",
    ko: "집계된 k-익명 선수 수요 뷰. 사용자 ID 없음 · 개인정보 없음 · 미성년자 제외.",
    ru: "Агрегированное, k-анонимное представление спроса игроков. Без ID пользователей · без персональных данных · несовершеннолетние исключены.",
    cs: "Agregovaný, k-anonymní pohled na poptávku hráčů. Bez ID uživatelů · bez osobních údajů · nezletilí vyloučeni.",
    nl: "Geaggregeerde, k-anonieme weergave van spelervraag. Geen gebruikers-ID's · geen PII · minderjarigen uitgesloten.",
    zh: "汇总的 k-匿名球员需求视图。无用户ID · 无个人身份信息 · 已排除未成年人。",
    de: "Aggregierte, k-anonyme Ansicht der Spielernachfrage. Keine Benutzer-IDs · keine personenbezogenen Daten · Minderjährige ausgeschlossen.",
  }, lang),
  privacyBanner: pick({
    en: "Phase 1 · Admin/Staff only · K-anonymity floor = 5 · Read-only audit trail",
    es: "Fase 1 · Solo Admin/Staff · K-anonimato = 5 · Auditoría de solo lectura",
    ca: "Fase 1 · Només administració/personal · Llindar K-anonimat = 5 · Traça d'auditoria només lectura",
    eu: "1. fasea · Administratzaileak/Langileak soilik · K-anonimotasun minimoa = 5 · Irakurtzeko soilik auditoria-arrastoa",
    gl: "Fase 1 · Só Admin/Staff · Límite K-anonimato = 5 · Rexistro de auditoría de só lectura",
    ar: "المرحلة 1 · المسؤولون/الموظفون فقط · حد إخفاء الهوية = 5 · سجل تدقيق للقراءة فقط",
    he: "שלב 1 · מנהלים/צוות בלבד · רף K-anonymity = 5 · מסלול ביקורת לקריאה בלבד",
    it: "Fase 1 · Solo amministratori/staff · Soglia k-anonimato = 5 · Registro di controllo in sola lettura",
    fr: "Phase 1 · Admin/Personnel uniquement · Seuil k-anonymat = 5 · Piste d'audit en lecture seule",
    "pt-br": "Fase 1 · Apenas Admin/Staff · Piso de K-anonimato = 5 · Trilha de auditoria somente leitura",
    ja: "フェーズ1 · 管理者/スタッフのみ · K-匿名性下限 = 5 · 読み取り専用監査証跡",
    hi: "चरण 1 · केवल व्यवस्थापक/स्टाफ · K-गुमनामी न्यूनतम = 5 · केवल-पढ़ने योग्य ऑडिट ट्रेल",
    ko: "1단계 · 관리자/스태프 전용 · K-익명성 기준 = 5 · 읽기 전용 감사 추적",
    ru: "Этап 1 · Только администраторы/персонал · Порог k-анонимности = 5 · Аудит только для чтения",
    cs: "Fáze 1 · Pouze administrátoři/Zaměstnanci · Práh K-anonymity = 5 · Auditní záznam pouze pro čtení",
    nl: "Fase 1 · Alleen beheerder/personeel · K-anonimiteitsvloer = 5 · Alleen-lezen controlespoor",
    zh: "第1阶段 · 仅限管理员/工作人员 · K-匿名下限 = 5 · 只读审计跟踪",
    de: "Phase 1 · Nur Admin/Mitarbeiter · K-Anonymitätsschwelle = 5 · Schreibgeschützter Prüfpfad",
  }, lang),
  // Hero block (DemandSummaryHero).
  heroLabel: pick({
    en: "Cohort included in this snapshot",
    es: "Cohorte incluida en este snapshot",
    ca: "Cohort inclosa en aquesta instantània",
    eu: "Argazki honetan sartutako kohortea",
    gl: "Cohorte incluída nesta instantánea",
    ar: "الفئة المشمولة في هذه اللقطة",
    he: "קבוצה כלולה בתמונת מצב זו",
    it: "Coorte inclusa in questa istantanea",
    fr: "Cohorte incluse dans cet instantané",
    "pt-br": "Coorte incluída neste snapshot",
    ja: "このスナップショットに含まれるコホート",
    hi: "इस स्नैपशॉट में शामिल समूह",
    ko: "이 스냅샷에 포함된 코호트",
    ru: "Когорта, включённая в этот снимок",
    cs: "Kohorta zahrnuta v tomto snímku",
    nl: "Cohort opgenomen in deze snapshot",
    zh: "此快照包含的群组",
    de: "In diesem Snapshot enthaltene Kohorte",
  }, lang),
  platformUsers: pick({
    en: "platform users",
    es: "usuarios de la plataforma",
    ca: "usuaris de la plataforma",
    eu: "plataforma-erabiltzaileak",
    gl: "usuarios da plataforma",
    ar: "مستخدمو المنصة",
    he: "משתמשי פלטפורמה",
    it: "utenti della piattaforma",
    fr: "utilisateurs de la plateforme",
    "pt-br": "usuários da plataforma",
    ja: "プラットフォームユーザー",
    hi: "प्लेटफ़ॉर्म उपयोगकर्ता",
    ko: "플랫폼 사용자",
    ru: "пользователей платформы",
    cs: "uživatelů platformy",
    nl: "platformgebruikers",
    zh: "平台用户",
    de: "Plattformnutzer",
  }, lang),
  coverage: pick({
    en: "Coverage",
    es: "Cobertura",
    ca: "Cobertura",
    eu: "Estaldura",
    gl: "Cobertura",
    ar: "التغطية",
    he: "כיסוי",
    it: "Copertura",
    fr: "Couverture",
    "pt-br": "Cobertura",
    ja: "カバレッジ",
    hi: "कवरेज",
    ko: "커버리지",
    ru: "Охват",
    cs: "Pokrytí",
    nl: "Dekking",
    zh: "覆盖范围",
    de: "Abdeckung",
  }, lang),
  insufficientData: pick({
    en: "Insufficient sample (less than 50 included users). Treat trend signals with caution.",
    es: "Muestra insuficiente (menos de 50 usuarios incluidos). Toma las tendencias con cautela.",
    ca: "Mostra insuficient (menys de 50 usuaris inclosos). Tracteu els senyals de tendència amb precaució.",
    eu: "Lagin nahikoa ez (50 erabiltzaile baino gutxiago). Tratatu joera-seinaleak kontuz.",
    gl: "Mostra insuficiente (menos de 50 usuarios incluídos). Trata os sinais de tendencia con precaución.",
    ar: "عينة غير كافية (أقل من 50 مستخدم مُدرج). تعامل مع إشارات الاتجاه بحذر.",
    he: "מדגם לא מספיק (פחות מ-50 משתמשים כלולים). יש להתייחס לאותות המגמה בזהירות.",
    it: "Campione insufficiente (meno di 50 utenti inclusi). Trattare i segnali di tendenza con cautela.",
    fr: "Échantillon insuffisant (moins de 50 utilisateurs inclus). Interpréter les signaux de tendance avec prudence.",
    "pt-br": "Amostra insuficiente (menos de 50 usuários incluídos). Trate sinais de tendência com cautela.",
    ja: "サンプル不足（含まれるユーザーが50人未満）。トレンドシグナルは慎重に扱ってください。",
    hi: "अपर्याप्त नमूना (50 से कम शामिल उपयोगकर्ता)। प्रवृत्ति संकेतों को सावधानी से लें।",
    ko: "샘플 부족 (포함된 사용자 50명 미만). 추세 신호는 주의하여 해석하십시오.",
    ru: "Недостаточная выборка (менее 50 включённых пользователей). Относитесь к сигналам трендов с осторожностью.",
    cs: "Nedostatečný vzorek (méně než 50 zahrnutých uživatelů). Ke trendovým signálům přistupujte obezřetně.",
    nl: "Onvoldoende steekproef (minder dan 50 opgenomen gebruikers). Behandel trendsignalen met voorzichtigheid.",
    zh: "样本不足(包含用户少于50个)。请谨慎对待趋势信号。",
    de: "Unzureichende Stichprobe (weniger als 50 einbezogene Nutzer). Trendsignale mit Vorsicht behandeln.",
  }, lang),
  noSnapshot: pick({
    en: "No snapshot has been generated yet. Click \"Regenerate snapshot\" to build the first one.",
    es: "Aún no hay snapshot. Pulsa \"Regenerar snapshot\" para construir el primero.",
    ca: "Encara no s'ha generat cap instantània. Feu clic a «Regenera instantània» per crear-ne la primera.",
    eu: "Ez da argazkirik sortu oraindik. Egin klik \"Sortu argazkia berriro\" lehenengoa eraikitzeko.",
    gl: "Aínda non se xerou ningunha instantánea. Preme \"Rexenerar instantánea\" para crear a primeira.",
    ar: "لم يتم إنشاء لقطة بعد. انقر على \"إعادة إنشاء اللقطة\" لبناء الأولى.",
    he: "טרם נוצרה תמונת מצב. לחץ על \"יצירה מחדש של תמונת מצב\" כדי לבנות את הראשונה.",
    it: "Nessuna istantanea è stata ancora generata. Fare clic su \"Rigenera istantanea\" per crearne la prima.",
    fr: "Aucun instantané n'a encore été généré. Cliquez sur « Régénérer l'instantané » pour créer le premier.",
    "pt-br": "Nenhum snapshot foi gerado ainda. Clique em \"Regenerar snapshot\" para criar o primeiro.",
    ja: "スナップショットはまだ生成されていません。「スナップショットを再生成」をクリックして最初のスナップショットを作成してください。",
    hi: "अभी तक कोई स्नैपशॉट उत्पन्न नहीं हुआ है। पहला बनाने के लिए \"स्नैपशॉट पुनर्जनित करें\" पर क्लिक करें।",
    ko: "아직 스냅샷이 생성되지 않았습니다. 첫 번째 스냅샷을 생성하려면 \"스냅샷 재생성\"을 클릭하십시오.",
    ru: "Снимок ещё не был сформирован. Нажмите «Пересоздать снимок», чтобы создать первый.",
    cs: "Zatím nebyl vygenerován žádný snímek. Klikněte na \"Regenerovat snímek\" a vytvořte první.",
    nl: "Er is nog geen snapshot gegenereerd. Klik op \"Snapshot opnieuw genereren\" om de eerste aan te maken.",
    zh: "尚未生成快照。点击\"重新生成快照\"以创建第一个。",
    de: "Es wurde noch kein Snapshot generiert. Klicken Sie auf „Snapshot neu generieren\", um den ersten zu erstellen.",
  }, lang),
  // Section headings.
  brandsTitle: pick({
    en: "Top brands",
    es: "Top marcas",
    ca: "Marques principals",
    eu: "Marka nagusiak",
    gl: "Marcas principais",
    ar: "أهم العلامات التجارية",
    he: "מותגים מובילים",
    it: "Marchi principali",
    fr: "Principales marques",
    "pt-br": "Principais marcas",
    ja: "トップブランド",
    hi: "शीर्ष ब्रांड",
    ko: "상위 브랜드",
    ru: "Топ брендов",
    cs: "Nejoblíbenější značky",
    nl: "Topmerken",
    zh: "热门品牌",
    de: "Top-Marken",
  }, lang),
  productCategoriesTitle: pick({
    en: "Top product categories",
    es: "Top categorías de producto",
    ca: "Categories de producte principals",
    eu: "Produktu-kategoria nagusiak",
    gl: "Categorías de produto principais",
    ar: "أهم فئات المنتجات",
    he: "קטגוריות מוצרים מובילות",
    it: "Categorie di prodotti principali",
    fr: "Principales catégories de produits",
    "pt-br": "Principais categorias de produtos",
    ja: "トップ製品カテゴリ",
    hi: "शीर्ष उत्पाद श्रेणियां",
    ko: "상위 제품 카테고리",
    ru: "Топ категорий товаров",
    cs: "Nejoblíbenější kategorie produktů",
    nl: "Topproductcategorieën",
    zh: "热门产品类别",
    de: "Top-Produktkategorien",
  }, lang),
  sponsorshipCategoriesTitle: pick({
    en: "Top sponsorship categories",
    es: "Top categorías de patrocinio",
    ca: "Categories de patrocini principals",
    eu: "Babesle-kategoria nagusiak",
    gl: "Categorías de patrocinio principais",
    ar: "أهم فئات الرعاية",
    he: "קטגוריות חסות מובילות",
    it: "Categorie di sponsorizzazione principali",
    fr: "Principales catégories de parrainage",
    "pt-br": "Principais categorias de patrocínio",
    ja: "トップスポンサーシップカテゴリ",
    hi: "शीर्ष प्रायोजन श्रेणियां",
    ko: "상위 스폰서십 카테고리",
    ru: "Топ категорий спонсорства",
    cs: "Nejoblíbenější kategorie sponzorství",
    nl: "Topsponsoringcategorieën",
    zh: "热门赞助类别",
    de: "Top-Sponsoring-Kategorien",
  }, lang),
  // Table headers.
  colLabel: pick({
    en: "Slug · friendly label",
    es: "Slug · etiqueta",
    ca: "Slug · etiqueta descriptiva",
    eu: "Slug · etiketa adiskidetsua",
    gl: "Slug · etiqueta amigable",
    ar: "المعرّف · التسمية الودية",
    he: "מזהה · תווית ידידותית",
    it: "Slug · etichetta descrittiva",
    fr: "Slug · libellé convivial",
    "pt-br": "Slug · rótulo amigável",
    ja: "スラッグ · わかりやすいラベル",
    hi: "स्लग · अनुकूल लेबल",
    ko: "슬러그 · 친화적 레이블",
    ru: "Слаг · понятная метка",
    cs: "Slug · popisný název",
    nl: "Slug · vriendelijk label",
    zh: "短标识 · 友好标签",
    de: "Slug · Freundliche Bezeichnung",
  }, lang),
  colCount: pick({
    en: "Users",
    es: "Usuarios",
    ca: "Usuaris",
    eu: "Erabiltzaileak",
    gl: "Usuarios",
    ar: "المستخدمون",
    he: "משתמשים",
    it: "Utenti",
    fr: "Utilisateurs",
    "pt-br": "Usuários",
    ja: "ユーザー",
    hi: "उपयोगकर्ता",
    ko: "사용자",
    ru: "Пользователи",
    cs: "Uživatelé",
    nl: "Gebruikers",
    zh: "用户",
    de: "Nutzer",
  }, lang),
  colSource: pick({
    en: "Source",
    es: "Origen",
    ca: "Origen",
    eu: "Iturria",
    gl: "Orixe",
    ar: "المصدر",
    he: "מקור",
    it: "Origine",
    fr: "Source",
    "pt-br": "Origem",
    ja: "ソース",
    hi: "स्रोत",
    ko: "출처",
    ru: "Источник",
    cs: "Zdroj",
    nl: "Bron",
    zh: "来源",
    de: "Quelle",
  }, lang),
  tableEmpty: pick({
    en: "No rows met the K-anonymity floor.",
    es: "Ninguna fila supera el umbral de K-anonimato.",
    ca: "Cap fila ha superat el llindar de K-anonimat.",
    eu: "Ez dago k-anonimotasun minimoa betetzen duen lerrorik.",
    gl: "Ningunha fila cumpriu o límite de K-anonimato.",
    ar: "لا توجد صفوف استوفت حد إخفاء الهوية.",
    he: "אף שורה לא עברה את רף K-anonymity.",
    zh: "没有行满足 K 匿名性下限。",
    it: "Nessuna riga ha raggiunto la soglia di K-anonimato.",
    fr: "Aucune ligne n'a atteint le seuil K-anonymat.",
    de: "Keine Zeilen erfüllten den K-Anonymitäts-Schwellenwert.",
    "pt-br": "Nenhuma linha atingiu o piso de K-anonimato.",
    ja: "K匿名性の基準を満たす行がありません。",
    hi: "कोई भी पंक्ति K-anonymity सीमा को पूरा नहीं करती।",
    ko: "K-익명성 임계값을 충족하는 행이 없습니다.",
    ru: "Ни одна строка не достигла порога K-анонимности.",
    cs: "Žádné řádky nesplnily práh K-anonymity.",
    nl: "Geen rijen voldeden aan de K-anonimiteitsdrempel.",
  }, lang),
  noPrior: pick({
    en: "No prior snapshot to compare against",
    es: "Sin snapshot previo para comparar",
    ca: "No hi ha instantània prèvia per comparar",
    eu: "Ez dago aurreko argazkirik konparatzeko",
    gl: "Non hai instantánea previa coa que comparar",
    ar: "لا توجد لقطة سابقة للمقارنة",
    he: "אין תמונת מצב קודמת להשוואה",
    zh: "没有先前快照可供对比",
    it: "Nessuna istantanea precedente con cui confrontare",
    fr: "Aucun instantané précédent pour comparaison",
    de: "Kein früherer Snapshot zum Vergleich vorhanden",
    "pt-br": "Nenhum snapshot anterior para comparação",
    ja: "比較対象となる過去のスナップショットがありません",
    hi: "तुलना के लिए कोई पूर्व स्नैपशॉट नहीं",
    ko: "비교할 이전 스냅샷이 없습니다",
    ru: "Нет предыдущего снимка для сравнения",
    cs: "Žádný předchozí snímek k porovnání",
    nl: "Geen eerdere momentopname om mee te vergelijken",
  }, lang),
  // Unknown / custom_other panel.
  unknownTitle: pick({
    en: "Unknown free-text terms (catalog expansion candidates)",
    es: "Términos libres no clasificados (candidatos a catálogo)",
    ca: "Termes de text lliure desconeguts (candidats per expansió de catàleg)",
    eu: "Testu libre-termino ezezagunak (katalogoa hedatzeko hautagaiak)",
    gl: "Termos de texto libre descoñecidos (candidatos para expansión do catálogo)",
    ar: "مصطلحات نصية حرة غير معروفة (مرشحة لتوسيع الكتالوج)",
    he: "מונחי טקסט חופשי לא ידועים (מועמדים להרחבת קטלוג)",
    zh: "未知自由文本术语（目录扩展候选）",
    it: "Termini di testo libero sconosciuti (candidati per l'espansione del catalogo)",
    fr: "Termes libres inconnus (candidats à l'expansion du catalogue)",
    de: "Unbekannte Freitext-Begriffe (Kandidaten für Katalogerweiterung)",
    "pt-br": "Termos de texto livre desconhecidos (candidatos a expansão de catálogo)",
    ja: "未知の自由記述用語（カタログ拡張候補）",
    hi: "अज्ञात मुक्त-पाठ शब्द (कैटलॉग विस्तार उम्मीदवार)",
    ko: "알 수 없는 자유 텍스트 용어 (카탈로그 확장 후보)",
    ru: "Неизвестные свободные термины (кандидаты на расширение каталога)",
    cs: "Neznámé volnotextové termíny (kandidáti na rozšíření katalogu)",
    nl: "Onbekende vrije-teksttermen (kandidaten voor catalogusuitbreiding)",
  }, lang),
  unknownSubtitle: pick({
    en: "Free-text values typed by 5+ users that did not match any catalog alias. Use these as input when expanding the seed catalogs.",
    es: "Valores libres tecleados por 5 o más usuarios que no coinciden con ningún alias del catálogo. Úsalos para ampliar los catálogos semilla.",
    ca: "Valors de text lliure introduïts per 5+ usuaris que no coincideixen amb cap àlies de catàleg. Utilitzeu-los com a entrada per ampliar els catàlegs inicials.",
    eu: "5+ erabiltzailek idatzitako testu libreko balioak ez datoz bat katalogo-alias batekin ere. Erabili hauek jatorrizko katalogoak hedatzean sarrera gisa.",
    gl: "Valores de texto libre introducidos por 5+ usuarios que non coincidiron con ningún alias do catálogo. Utiliza estes como entrada ao expandir os catálogos sementa.",
    ar: "قيم نصية حرة كتبها 5+ مستخدمين ولم تطابق أي اسم مستعار في الكتالوج. استخدمها كمُدخل عند توسيع كتالوجات البذور.",
    he: "ערכי טקסט חופשי שהוזנו על ידי 5+ משתמשים שלא התאימו לאף כינוי קטלוג. השתמש בהם כקלט בעת הרחבת קטלוגי הבסיס.",
    zh: "5 个以上用户输入的自由文本值，与任何目录别名都不匹配。在扩展种子目录时将这些作为输入。",
    it: "Valori di testo libero digitati da 5+ utenti che non corrispondono ad alcun alias del catalogo. Utilizzali come input per espandere i cataloghi di base.",
    fr: "Valeurs en texte libre saisies par 5+ utilisateurs qui ne correspondent à aucun alias du catalogue. Utilisez-les comme données d'entrée lors de l'expansion des catalogues de référence.",
    de: "Freitext-Werte, die von mindestens 5 Nutzern eingegeben wurden und keinem Katalog-Alias entsprachen. Diese als Input für die Erweiterung der Stamm-Kataloge verwenden.",
    "pt-br": "Valores de texto livre digitados por 5+ usuários que não corresponderam a nenhum alias do catálogo. Use-os como entrada ao expandir os catálogos iniciais.",
    ja: "5人以上のユーザーが入力した自由記述値のうち、カタログのエイリアスと一致しなかったもの。シードカタログを拡張する際の入力として使用してください。",
    hi: "5+ उपयोगकर्ताओं द्वारा टाइप किए गए मुक्त-पाठ मान जो किसी कैटलॉग उपनाम से मेल नहीं खाते। सीड कैटलॉग का विस्तार करते समय इनका इनपुट के रूप में उपयोग करें।",
    ko: "카탈로그 별칭과 일치하지 않는 5명 이상의 사용자가 입력한 자유 텍스트 값입니다. 시드 카탈로그를 확장할 때 이를 입력으로 사용하십시오.",
    ru: "Значения свободного текста, введённые 5+ пользователями, которые не совпали ни с одним псевдонимом каталога. Используйте их в качестве исходных данных при расширении базовых каталогов.",
    cs: "Volnotextové hodnoty zadané 5+ uživateli, které neodpovídaly žádnému aliasu katalogu. Použijte je jako vstup při rozšiřování základních katalogů.",
    nl: "Vrije-tekstwaarden ingevoerd door 5+ gebruikers die niet overeenkwamen met een catalogusalias. Gebruik deze als input bij het uitbreiden van de basiscatalogi.",
  }, lang),
  unknownEmpty: pick({
    en: "No unknown terms cleared the K-anonymity floor.",
    es: "Ningún término libre superó el umbral de K-anonimato.",
    ca: "Cap terme desconegut ha superat el llindar de K-anonimat.",
    eu: "Ez dago termino ezezagunik k-anonimotasun minimoa gainditzen duenik.",
    gl: "Ningún termo descoñecido superou o límite de K-anonimato.",
    ar: "لا توجد مصطلحات غير معروفة تجاوزت حد إخفاء الهوية.",
    he: "אף מונח לא ידוע עבר את רף K-anonymity.",
    zh: "没有未知术语满足 K 匿名性下限。",
    it: "Nessun termine sconosciuto ha raggiunto la soglia di K-anonimato.",
    fr: "Aucun terme inconnu n'a atteint le seuil K-anonymat.",
    de: "Keine unbekannten Begriffe erfüllten den K-Anonymitäts-Schwellenwert.",
    "pt-br": "Nenhum termo desconhecido atingiu o piso de K-anonimato.",
    ja: "K匿名性の基準を満たす未知の用語がありません。",
    hi: "कोई भी अज्ञात शब्द K-anonymity सीमा को पार नहीं करता।",
    ko: "K-익명성 임계값을 통과한 알 수 없는 용어가 없습니다.",
    ru: "Ни один неизвестный термин не достиг порога K-анонимности.",
    cs: "Žádné neznámé termíny nesplnily práh K-anonymity.",
    nl: "Geen onbekende termen voldeden aan de K-anonimiteitsdrempel.",
  }, lang),
  fieldBrands: pick({
    en: "brand",
    es: "marca",
    ca: "marca",
    eu: "marka",
    gl: "marca",
    ar: "علامة تجارية",
    he: "מותג",
    zh: "品牌",
    it: "brand",
    fr: "marque",
    de: "Marke",
    "pt-br": "marca",
    ja: "ブランド",
    hi: "ब्रांड",
    ko: "브랜드",
    ru: "бренд",
    cs: "značka",
    nl: "merk",
  }, lang),
  fieldProductCats: pick({
    en: "product",
    es: "producto",
    ca: "producte",
    eu: "produktua",
    gl: "produto",
    ar: "منتج",
    he: "מוצר",
    zh: "产品",
    it: "prodotto",
    fr: "produit",
    de: "Produkt",
    "pt-br": "produto",
    ja: "製品",
    hi: "उत्पाद",
    ko: "제품",
    ru: "продукт",
    cs: "produkt",
    nl: "product",
  }, lang),
  fieldSponsorCats: pick({
    en: "sponsorship",
    es: "patrocinio",
    ca: "patrocini",
    eu: "babeslea",
    gl: "patrocinio",
    ar: "رعاية",
    he: "חסות",
    zh: "赞助",
    it: "sponsorizzazione",
    fr: "sponsoring",
    de: "Sponsoring",
    "pt-br": "patrocínio",
    ja: "スポンサーシップ",
    hi: "प्रायोजन",
    ko: "스폰서십",
    ru: "спонсорство",
    cs: "sponzorství",
    nl: "sponsoring",
  }, lang),
  // Geo + collapsed footer.
  geoTitle: pick({
    en: "Geographic breakdown",
    es: "Distribución geográfica",
    ca: "Desglossament geogràfic",
    eu: "Banaketa geografikoa",
    gl: "Desglose xeográfico",
    ar: "التوزيع الجغرافي",
    he: "פילוח גאוגרפי",
    zh: "地理分布",
    it: "Ripartizione geografica",
    fr: "Répartition géographique",
    de: "Geografische Aufschlüsselung",
    "pt-br": "Detalhamento geográfico",
    ja: "地域別内訳",
    hi: "भौगोलिक विभाजन",
    ko: "지역별 분석",
    ru: "Разбивка по географии",
    cs: "Geografické rozložení",
    nl: "Geografische verdeling",
  }, lang),
  geoEmpty: pick({
    en: "No country bucket cleared the K-anonymity floor.",
    es: "Ningún país superó el umbral de K-anonimato.",
    ca: "Cap agrupació per país ha superat el llindar de K-anonimat.",
    eu: "Ez dago herrialde-multzorik k-anonimotasun minimoa gainditzen duenik.",
    gl: "Ningún grupo por país superou o límite de K-anonimato.",
    ar: "لم تتجاوز أي مجموعة قُطرية حد إخفاء الهوية.",
    he: "אף קבוצת מדינה לא עברה את רף K-anonymity.",
    zh: "没有国家分组满足 K 匿名性下限。",
    it: "Nessun gruppo di paesi ha raggiunto la soglia di K-anonimato.",
    fr: "Aucun groupe de pays n'a atteint le seuil K-anonymat.",
    de: "Kein Länder-Bucket erfüllte den K-Anonymitäts-Schwellenwert.",
    "pt-br": "Nenhum grupo de país atingiu o piso de K-anonimato.",
    ja: "K匿名性の基準を満たす国別グループがありません。",
    hi: "कोई भी देश समूह K-anonymity सीमा को पार नहीं करता।",
    ko: "K-익명성 임계값을 통과한 국가 버킷이 없습니다.",
    ru: "Ни одна группа стран не достигла порога K-анонимности.",
    cs: "Žádná skupina zemí nesplnila práh K-anonymity.",
    nl: "Geen landcategorie voldeed aan de K-anonimiteitsdrempel.",
  }, lang),
  collapsedNotice: pick({
    en: "{n} values were too small to surface (collapsed below K=5).",
    es: "{n} valores eran demasiado pequeños para mostrarse (colapsados por K=5).",
    ca: "{n} valors eren massa petits per mostrar (reduïts per sota de K=5).",
    eu: "{n} balio txikiegiak izan dira azaleratzeko (K=5 azpitik kolapsatuta).",
    gl: "{n} valores eran demasiado pequenos para mostralos (contraídos por debaixo de K=5).",
    ar: "{n} قيمة كانت صغيرة جداً للظهور (مُطوية تحت حد 5).",
    he: "{n} ערכים היו קטנים מדי להצגה (כווצו מתחת ל-K=5).",
    zh: "{n} 个值过小无法显示（已折叠至 K=5 以下）。",
    it: "{n} valori erano troppo piccoli per essere visualizzati (raggruppati sotto K=5).",
    fr: "{n} valeurs étaient trop petites pour être affichées (regroupées sous K=5).",
    de: "{n} Werte waren zu klein zur Anzeige (unter K=5 zusammengefasst).",
    "pt-br": "{n} valores eram muito pequenos para serem exibidos (agrupados abaixo de K=5).",
    ja: "{n}件の値は小さすぎて表示できません（K=5未満に集約）。",
    hi: "{n} मान सतह पर लाने के लिए बहुत छोटे थे (K=5 से नीचे संकुचित)।",
    ko: "{n}개의 값이 너무 작아 표시되지 않습니다 (K=5 미만으로 축소됨).",
    ru: "{n} значений были слишком малы для отображения (свёрнуты ниже K=5).",
    cs: "{n} hodnot bylo příliš malých pro zobrazení (sbaleno pod K=5).",
    nl: "{n} waarden waren te klein om weer te geven (samengevoegd onder K=5).",
  }, lang),
  // Actions.
  refresh: pick({
    en: "Reload",
    es: "Recargar",
    ca: "Recarrega",
    eu: "Birkargatu",
    gl: "Recargar",
    ar: "إعادة تحميل",
    he: "טעינה מחדש",
    zh: "重新加载",
    it: "Ricarica",
    fr: "Recharger",
    de: "Neu laden",
    "pt-br": "Recarregar",
    ja: "再読み込み",
    hi: "पुनः लोड करें",
    ko: "새로고침",
    ru: "Перезагрузить",
    cs: "Znovu načíst",
    nl: "Opnieuw laden",
  }, lang),
  runSnapshot: pick({
    en: "Regenerate snapshot",
    es: "Regenerar snapshot",
    ca: "Regenera instantània",
    eu: "Sortu argazkia berriro",
    gl: "Rexenerar instantánea",
    ar: "إعادة إنشاء اللقطة",
    he: "יצירה מחדש של תמונת מצב",
    zh: "重新生成快照",
    it: "Rigenera istantanea",
    fr: "Régénérer l'instantané",
    de: "Snapshot neu generieren",
    "pt-br": "Regenerar snapshot",
    ja: "スナップショットを再生成",
    hi: "स्नैपशॉट पुनर्जनित करें",
    ko: "스냅샷 재생성",
    ru: "Пересоздать снимок",
    cs: "Znovu vygenerovat snímek",
    nl: "Momentopname opnieuw genereren",
  }, lang),
  runningSnapshot: pick({
    en: "Regenerating…",
    es: "Regenerando…",
    ca: "Regenerant…",
    eu: "Sortzen…",
    gl: "Rexenerando…",
    ar: "جارٍ إعادة الإنشاء…",
    he: "יוצר מחדש…",
    zh: "正在重新生成…",
    it: "Rigenerazione in corso…",
    fr: "Régénération…",
    de: "Wird neu generiert…",
    "pt-br": "Regenerando…",
    ja: "再生成中…",
    hi: "पुनर्जनित हो रहा है…",
    ko: "재생성 중…",
    ru: "Пересоздание…",
    cs: "Generování…",
    nl: "Opnieuw genereren…",
  }, lang),
  runConfirmTitle: pick({
    en: "Regenerate today's snapshot?",
    es: "¿Regenerar el snapshot de hoy?",
    ca: "Voleu regenerar la instantània d'avui?",
    eu: "Sortu gaurko argazkia berriro?",
    gl: "Rexenerar a instantánea de hoxe?",
    ar: "إعادة إنشاء لقطة اليوم؟",
    he: "ליצור מחדש את תמונת המצב של היום?",
    zh: "重新生成今日快照？",
    it: "Rigenerare l'istantanea di oggi?",
    fr: "Régénérer l'instantané d'aujourd'hui ?",
    de: "Heutigen Snapshot neu generieren?",
    "pt-br": "Regenerar snapshot de hoje?",
    ja: "今日のスナップショットを再生成しますか？",
    hi: "आज का स्नैपशॉट पुनर्जनित करें?",
    ko: "오늘의 스냅샷을 재생성하시겠습니까?",
    ru: "Пересоздать сегодняшний снимок?",
    cs: "Znovu vygenerovat dnešní snímek?",
    nl: "Momentopname van vandaag opnieuw genereren?",
  }, lang),
  runConfirmBody: pick({
    en: "This re-runs the aggregation against the current user base and overwrites today's snapshot. The action is logged in the access log.",
    es: "Esto vuelve a ejecutar la agregación sobre la base actual y sobrescribe el snapshot de hoy. La acción queda registrada en el log de acceso.",
    ca: "Això torna a executar l'agregació contra la base d'usuaris actual i sobreescriu la instantània d'avui. L'acció queda registrada al registre d'accés.",
    eu: "Honek agregazio berregitura exekutatzen du uneko erabiltzaile oinarriaren aurka eta gaurko argazkia gainidazten du. Ekintza sarbide erregistroan jasotzen da.",
    gl: "Isto volve executar a agregación contra a base de usuarios actual e sobrescribe a instantánea de hoxe. A acción queda rexistrada no rexistro de acceso.",
    ar: "هذا الإجراء يعيد تشغيل التجميع مقابل قاعدة المستخدمين الحالية ويستبدل لقطة اليوم. يتم تسجيل الإجراء في سجل الوصول.",
    he: "פעולה זו מריצה מחדש את הצבירה מול בסיס המשתמשים הנוכחי ודורסת את תמונת המצב של היום. הפעולה נרשמת ביומן הגישה.",
    zh: "此操作将针对当前用户群重新运行聚合，并覆盖今日快照。该操作将记录在访问日志中。",
    it: "Questa operazione riesegue l'aggregazione sulla base utenti corrente e sovrascrive l'istantanea odierna. L'azione viene registrata nel log di accesso.",
    fr: "Cette opération relance l'agrégation sur la base d'utilisateurs actuelle et écrase l'instantané du jour. L'action est enregistrée dans le journal d'accès.",
    de: "Dadurch wird die Aggregation gegen die aktuelle Nutzerbasis erneut ausgeführt und der heutige Snapshot überschrieben. Die Aktion wird im Zugriffsprotokoll erfasst.",
    "pt-br": "Isso reexecuta a agregação com base na base de usuários atual e sobrescreve o snapshot de hoje. A ação é registrada no log de acesso.",
    ja: "現在のユーザーベースに対して集計を再実行し、本日のスナップショットを上書きします。この操作はアクセスログに記録されます。",
    hi: "यह वर्तमान उपयोगकर्ता आधार के विरुद्ध एकत्रीकरण को पुनः चलाता है और आज के स्नैपशॉट को अधिलेखित करता है। यह क्रिया एक्सेस लॉग में दर्ज की जाती है।",
    ko: "현재 사용자 기반에 대해 집계를 재실행하고 오늘의 스냅샷을 덮어씁니다. 이 작업은 액세스 로그에 기록됩니다.",
    ru: "Это повторно запускает агрегацию для текущей базы пользователей и перезаписывает сегодняшний снимок. Действие регистрируется в журнале доступа.",
    cs: "Tato akce znovu spustí agregaci proti aktuální uživatelské základně a přepíše dnešní snímek. Akce je zaznamenána v přístupovém protokolu.",
    nl: "Dit voert de aggregatie opnieuw uit op de huidige gebruikersbasis en overschrijft de snapshot van vandaag. De actie wordt geregistreerd in het toegangslogboek.",
  }, lang),
  runConfirmYes: pick({
    en: "Yes, regenerate",
    es: "Sí, regenerar",
    ca: "Sí, regenera",
    eu: "Bai, birsortu",
    gl: "Si, rexenerar",
    ar: "نعم، إعادة الإنشاء",
    he: "כן, צור מחדש",
    zh: "是的，重新生成",
    it: "Sì, rigenera",
    fr: "Oui, régénérer",
    de: "Ja, neu generieren",
    "pt-br": "Sim, regenerar",
    ja: "はい、再生成する",
    hi: "हां, पुनः उत्पन्न करें",
    ko: "예, 재생성",
    ru: "Да, перегенерировать",
    cs: "Ano, regenerovat",
    nl: "Ja, opnieuw genereren",
  }, lang),
  runConfirmNo: pick({
    en: "Cancel",
    es: "Cancelar",
    ca: "Cancel·la",
    eu: "Utzi",
    gl: "Cancelar",
    ar: "إلغاء",
    he: "ביטול",
    zh: "取消",
    it: "Annulla",
    fr: "Annuler",
    de: "Abbrechen",
    "pt-br": "Cancelar",
    ja: "キャンセル",
    hi: "रद्द करें",
    ko: "취소",
    ru: "Отмена",
    cs: "Zrušit",
    nl: "Annuleren",
  }, lang),
  // Toasts.
  runSuccess: pick({
    en: "Snapshot regenerated.",
    es: "Snapshot regenerado.",
    ca: "Instantània regenerada.",
    eu: "Argazkia bირsortu da.",
    gl: "Instantánea rexenerada.",
    ar: "تم إعادة إنشاء اللقطة.",
    he: "תמונת המצב נוצרה מחדש.",
    zh: "快照已重新生成。",
    it: "Istantanea rigenerata.",
    fr: "Instantané régénéré.",
    de: "Snapshot neu generiert.",
    "pt-br": "Snapshot regenerado.",
    ja: "スナップショットを再生成しました。",
    hi: "स्नैपशॉट पुनः उत्पन्न किया गया।",
    ko: "스냅샷이 재생성되었습니다.",
    ru: "Снимок перегенерирован.",
    cs: "Snímek regenerován.",
    nl: "Snapshot opnieuw gegenereerd.",
  }, lang),
  runFailed: pick({
    en: "Could not regenerate snapshot.",
    es: "No se pudo regenerar el snapshot.",
    ca: "No s'ha pogut regenerar la instantània.",
    eu: "Ezin izan da argazkia birsortu.",
    gl: "Non se puido rexenerar a instantánea.",
    ar: "تعذر إعادة إنشاء اللقطة.",
    he: "לא ניתן היה ליצור מחדש את תמונת המצב.",
    zh: "无法重新生成快照。",
    it: "Impossibile rigenerare l'istantanea.",
    fr: "Impossible de régénérer l'instantané.",
    de: "Snapshot konnte nicht neu generiert werden.",
    "pt-br": "Não foi possível regenerar o snapshot.",
    ja: "スナップショットを再生成できませんでした。",
    hi: "स्नैपशॉट पुनः उत्पन्न नहीं किया जा सका।",
    ko: "스냅샷을 재생성할 수 없습니다.",
    ru: "Не удалось перегенерировать снимок.",
    cs: "Snímek nelze regenerovat.",
    nl: "Kon snapshot niet opnieuw genereren.",
  }, lang),
  loadFailed: pick({
    en: "Could not load demand snapshot.",
    es: "No se pudo cargar el snapshot de demanda.",
    ca: "No s'ha pogut carregar la instantània de demanda.",
    eu: "Ezin izan da eskari argazkia kargatu.",
    gl: "Non se puido cargar a instantánea de demanda.",
    ar: "تعذر تحميل لقطة الطلب.",
    he: "לא ניתן היה לטעון את תמונת מצב הביקוש.",
    zh: "无法加载需求快照。",
    it: "Impossibile caricare l'istantanea della domanda.",
    fr: "Impossible de charger l'instantané de demande.",
    de: "Demand-Snapshot konnte nicht geladen werden.",
    "pt-br": "Não foi possível carregar o snapshot de demanda.",
    ja: "需要スナップショットを読み込めませんでした。",
    hi: "डिमांड स्नैपशॉट लोड नहीं किया जा सका।",
    ko: "수요 스냅샷을 불러올 수 없습니다.",
    ru: "Не удалось загрузить снимок спроса.",
    cs: "Snímek poptávky nelze načíst.",
    nl: "Kon vraag-snapshot niet laden.",
  }, lang),
  // Disabled state.
  disabledTitle: pick({
    en: "Marketplace module disabled",
    es: "Módulo Marketplace deshabilitado",
    ca: "Mòdul de marketplace desactivat",
    eu: "Marketplace modulua desgaituta",
    gl: "Módulo Marketplace desactivado",
    ar: "وحدة السوق معطلة",
    he: "מודול Marketplace מושבת",
    zh: "市场模块已禁用",
    it: "Modulo Marketplace disabilitato",
    fr: "Module Marketplace désactivé",
    de: "Marketplace-Modul deaktiviert",
    "pt-br": "Módulo Marketplace desabilitado",
    ja: "マーケットプレイスモジュールが無効です",
    hi: "मार्केटप्लेस मॉड्यूल अक्षम है",
    ko: "마켓플레이스 모듈 비활성화됨",
    ru: "Модуль Marketplace отключен",
    cs: "Modul Marketplace zakázán",
    nl: "Marketplace-module uitgeschakeld",
  }, lang),
  disabledBody: pick({
    en: "Set MARKETPLACE_ENABLED=true in the backend environment to enable this surface.",
    es: "Activa MARKETPLACE_ENABLED=true en el entorno del backend para habilitar esta superficie.",
    ca: "Establiu MARKETPLACE_ENABLED=true a l'entorn del backend per activar aquesta superfície.",
    eu: "Ezarri MARKETPLACE_ENABLED=true backend ingurunean azalera hau gaitzeko.",
    gl: "Establece MARKETPLACE_ENABLED=true no entorno backend para activar esta superficie.",
    ar: "قم بتعيين MARKETPLACE_ENABLED=true في بيئة الخادم لتفعيل هذه الواجهة.",
    he: "הגדר MARKETPLACE_ENABLED=true בסביבת ה-backend כדי להפעיל משטח זה.",
    zh: "在后端环境中设置 MARKETPLACE_ENABLED=true 以启用此界面。",
    it: "Impostare MARKETPLACE_ENABLED=true nell'ambiente backend per abilitare questa interfaccia.",
    fr: "Définir MARKETPLACE_ENABLED=true dans l'environnement backend pour activer cette interface.",
    de: "Setzen Sie MARKETPLACE_ENABLED=true in der Backend-Umgebung, um diese Oberfläche zu aktivieren.",
    "pt-br": "Defina MARKETPLACE_ENABLED=true no ambiente backend para habilitar esta superfície.",
    ja: "この画面を有効にするには、バックエンド環境でMARKETPLACE_ENABLED=trueに設定してください。",
    hi: "इस सतह को सक्षम करने के लिए बैकएंड वातावरण में MARKETPLACE_ENABLED=true सेट करें।",
    ko: "이 화면을 활성화하려면 백엔드 환경에서 MARKETPLACE_ENABLED=true로 설정하세요.",
    ru: "Установите MARKETPLACE_ENABLED=true в окружении бэкенда, чтобы включить этот интерфейс.",
    cs: "Nastavte MARKETPLACE_ENABLED=true v backendovém prostředí pro aktivaci tohoto rozhraní.",
    nl: "Stel MARKETPLACE_ENABLED=true in de backend-omgeving in om deze interface in te schakelen.",
  }, lang),
  // Strategic readiness panel — visible when data is sparse (Feb 2026
  // product-readiness polish). Frames the empty state as "ready and
  // waiting" instead of "nothing here", so demos with sponsors/partners
  // read as a deliberate Phase-1 staging surface.
  readinessTitle: pick({
    en: "TEAM Connect · Phase 1 ready",
    es: "TEAM Connect · Fase 1 operativa",
    ca: "TEAM Connect · Fase 1 llesta",
    eu: "TEAM Connect · 1. fasea prest",
    gl: "TEAM Connect · Fase 1 lista",
    ar: "TEAM Connect · المرحلة 1 جاهزة",
    he: "TEAM Connect · שלב 1 מוכן",
    zh: "TEAM Connect · 第1阶段就绪",
    it: "TEAM Connect · Fase 1 pronta",
    fr: "TEAM Connect · Phase 1 prête",
    de: "TEAM Connect · Phase 1 bereit",
    "pt-br": "TEAM Connect · Fase 1 pronta",
    ja: "TEAM Connect · フェーズ1準備完了",
    hi: "TEAM Connect · फेज 1 तैयार",
    ko: "TEAM Connect · Phase 1 준비 완료",
    ru: "TEAM Connect · Фаза 1 готова",
    cs: "TEAM Connect · Fáze 1 připravena",
    nl: "TEAM Connect · Fase 1 gereed",
  }, lang),
  readinessSub: pick({
    en: "Demand Insights is live. Aggregated signal will appear here as users complete their Wishlist Preferences.",
    es: "Demand Insights está activo. La señal agregada aparecerá aquí a medida que los usuarios completen sus Preferencias de Wishlist.",
    ca: "Demand Insights està actiu. El senyal agregat apareixerà aquí a mesura que els usuaris completin les seves Preferències de Wishlist.",
    eu: "Demand Insights martxan dago. Seinale agregatua hemen agertuko da erabiltzaileek beren Nahi-zerrenda Hobespenak osatzen dituzten heinean.",
    gl: "Demand Insights está activo. O sinal agregado aparecerá aquí cando os usuarios completen as súas Preferencias da Lista de Desexos.",
    ar: "رؤى الطلب مفعّلة. ستظهر الإشارة المجمعة هنا عندما يكمل المستخدمون تفضيلات قائمة الرغبات.",
    he: "Demand Insights פעיל. אות מצטבר יופיע כאן כאשר משתמשים משלימים את העדפות רשימת המשאלות שלהם.",
    zh: "需求洞察已上线。当用户完成心愿单偏好设置后，聚合信号将显示在此处。",
    it: "Demand Insights è attivo. Il segnale aggregato apparirà qui quando gli utenti completano le loro Preferenze Wishlist.",
    fr: "Demand Insights est actif. Le signal agrégé apparaîtra ici lorsque les utilisateurs auront complété leurs Préférences de Liste de Souhaits.",
    de: "Demand Insights ist aktiv. Aggregierte Signale erscheinen hier, sobald Nutzer ihre Wunschlisten-Präferenzen vervollständigen.",
    "pt-br": "Demand Insights está no ar. O sinal agregado aparecerá aqui conforme os usuários preencherem suas Preferências da Lista de Desejos.",
    ja: "Demand Insightsが稼働中です。ユーザーがウィッシュリスト設定を完了すると、集計されたシグナルがここに表示されます。",
    hi: "डिमांड इनसाइट्स लाइव है। जब उपयोगकर्ता अपनी विशलिस्ट प्राथमिकताएं पूर्ण करेंगे तो एकत्रित संकेत यहां दिखाई देगा।",
    ko: "Demand Insights가 활성화되었습니다. 사용자가 위시리스트 환경설정을 완료하면 집계된 신호가 여기에 표시됩니다.",
    ru: "Demand Insights активен. Агрегированные данные появятся здесь, когда пользователи заполнят свои предпочтения списка пожеланий.",
    cs: "Demand Insights je aktivní. Agregovaný signál se zde zobrazí, jakmile uživatelé vyplní své Wishlist Preferences.",
    nl: "Demand Insights is live. Geaggregeerd signaal verschijnt hier zodra gebruikers hun Wishlist-voorkeuren hebben voltooid.",
  }, lang),
  readinessStatusMarketplace: pick({
    en: "Marketplace Phase 1 · active",
    es: "Marketplace Fase 1 · activo",
    ca: "Marketplace Fase 1 · actiu",
    eu: "Marketplace 1. fasea · aktibo",
    gl: "Marketplace Fase 1 · activo",
    ar: "المرحلة 1 للسوق · نشطة",
    he: "Marketplace שלב 1 · פעיל",
    zh: "市场第1阶段 · 活跃",
    it: "Marketplace Fase 1 · attivo",
    fr: "Marketplace Phase 1 · active",
    de: "Marketplace Phase 1 · aktiv",
    "pt-br": "Marketplace Fase 1 · ativo",
    ja: "マーケットプレイス フェーズ1 · 稼働中",
    hi: "मार्केटप्लेस फेज 1 · सक्रिय",
    ko: "Marketplace Phase 1 · 활성",
    ru: "Marketplace Фаза 1 · активна",
    cs: "Marketplace Fáze 1 · aktivní",
    nl: "Marketplace Fase 1 · actief",
  }, lang),
  readinessStatusWlp: pick({
    en: "Wishlist v2 · connected",
    es: "Wishlist v2 · conectado",
    ca: "Wishlist v2 · connectat",
    eu: "Nahi-zerrenda v2 · konektatuta",
    gl: "Wishlist v2 · conectada",
    ar: "قائمة الرغبات الإصدار 2 · متصلة",
    he: "Wishlist v2 · מחובר",
    zh: "心愿单 v2 · 已连接",
    it: "Wishlist v2 · connessa",
    fr: "Wishlist v2 · connectée",
    de: "Wishlist v2 · verbunden",
    "pt-br": "Lista de Desejos v2 · conectada",
    ja: "ウィッシュリスト v2 · 接続済み",
    hi: "विशलिस्ट v2 · कनेक्टेड",
    ko: "Wishlist v2 · 연결됨",
    ru: "Wishlist v2 · подключен",
    cs: "Wishlist v2 · připojeno",
    nl: "Wishlist v2 · verbonden",
  }, lang),
  readinessStatusKAnon: pick({
    en: "K-anonymity floor 5 · enforced",
    es: "K-anonimato 5 · aplicado",
    ca: "Llindar K-anonymity 5 · aplicat",
    eu: "K-anonimotasun muga 5 · aplikatuta",
    gl: "Límite K-anonymity 5 · aplicado",
    ar: "حد عدم الكشف عن الهوية K-anonymity 5 · مُطبّق",
    he: "רף K-anonymity 5 · נאכף",
    zh: "K-匿名性下限5 · 已强制执行",
    it: "Soglia K-anonymity 5 · applicata",
    fr: "Seuil K-anonymat 5 · appliqué",
    de: "K-Anonymitätsschwelle 5 · durchgesetzt",
    "pt-br": "Piso K-anonimato 5 · aplicado",
    ja: "K匿名性しきい値5 · 適用済み",
    hi: "K-anonymity फ्लोर 5 · लागू",
    ko: "K-익명성 최소값 5 · 적용됨",
    ru: "K-анонимность порог 5 · применяется",
    cs: "K-anonymity práh 5 · vynuceno",
    nl: "K-anonimiteit drempel 5 · afgedwongen",
  }, lang),
  readinessStatusPrivacy: pick({
    en: "Privacy-safe · no PII · no user IDs",
    es: "Privacidad segura · sin PII · sin user IDs",
    ca: "Privacitat segura · sense PII · sense ID d'usuari",
    eu: "Pribatutasun segurua · PII ez · erabiltzaile ID-rik ez",
    gl: "Privacidade segura · sen PII · sen ID de usuario",
    ar: "آمن للخصوصية · بدون معلومات شخصية · بدون معرّفات مستخدمين",
    he: "בטוח לפרטיות · ללא מידע מזהה · ללא מזהי משתמש",
    zh: "隐私安全 · 无PII · 无用户ID",
    it: "Privacy-safe · no PII · no ID utente",
    fr: "Conforme à la vie privée · sans PII · sans ID utilisateur",
    de: "Datenschutzsicher · keine personenbezogenen Daten · keine Nutzer-IDs",
    "pt-br": "Seguro para privacidade · sem PII · sem IDs de usuário",
    ja: "プライバシー保護 · 個人情報なし · ユーザーIDなし",
    hi: "गोपनीयता-सुरक्षित · कोई PII नहीं · कोई उपयोगकर्ता ID नहीं",
    ko: "개인정보 보호 · PII 없음 · 사용자 ID 없음",
    ru: "Безопасно для конфиденциальности · без персональных данных · без ID пользователей",
    cs: "Bezpečné pro soukromí · žádné PII · žádná ID uživatelů",
    nl: "Privacy-veilig · geen PII · geen gebruikers-ID's",
  }, lang),
  readinessStatusRacketLab: pick({
    en: "Racket Lab · off in Production",
    es: "Racket Lab · apagado en Producción",
    ca: "Racket Lab · desactivat a Producció",
    eu: "Racket Lab · itzalita Produkzioan",
    gl: "Racket Lab · desactivado en Produción",
    ar: "Racket Lab · معطل في الإنتاج",
    he: "Racket Lab · כבוי ב-Production",
    zh: "Racket Lab · 生产环境中已关闭",
    it: "Racket Lab · disattivato in Production",
    fr: "Racket Lab · désactivé en Production",
    de: "Racket Lab · aus in Production",
    "pt-br": "Racket Lab · desligado em Produção",
    ja: "Racket Lab · 本番環境でオフ",
    hi: "Racket Lab · प्रोडक्शन में बंद",
    ko: "Racket Lab · 프로덕션에서 비활성",
    ru: "Racket Lab · выключен в Production",
    cs: "Racket Lab · vypnuto v Produkci",
    nl: "Racket Lab · uit in Productie",
  }, lang),
  readinessStatusSponsorView: pick({
    en: "Sponsor view · not activated yet",
    es: "Vista sponsor · aún no activada",
    ca: "Vista de patrocinador · encara no activada",
    eu: "Babeslearen ikuspegia · ez oraindik aktibatuta",
    gl: "Vista Patrocinador · aínda non activada",
    ar: "عرض الرعاة · غير مفعّل بعد",
    he: "תצוגת ספונסר · טרם הופעלה",
    zh: "赞助商视图 · 尚未激活",
    it: "Vista sponsor · non ancora attivata",
    fr: "Vue sponsor · pas encore activée",
    de: "Sponsor-Ansicht · noch nicht aktiviert",
    "pt-br": "Visão do patrocinador · ainda não ativada",
    ja: "スポンサービュー · 未アクティブ化",
    hi: "प्रायोजक दृश्य · अभी सक्रिय नहीं",
    ko: "스폰서 보기 · 아직 활성화되지 않음",
    ru: "Представление спонсора · ещё не активировано",
    cs: "Pohled sponzora · zatím neaktivováno",
    nl: "Sponsorweergave · nog niet geactiveerd",
  }, lang),
  readinessAdoptionLabel: pick({
    en: "Adoption push (B3.1) status",
    es: "Push de adopción (B3.1)",
    ca: "Estat d'impuls d'adopció (B3.1)",
    eu: "Adopzio bultzadaren (B3.1) egoera",
    gl: "Estado do impulso de adopción (B3.1)",
    ar: "حالة دفع التبني (B3.1)",
    he: "סטטוס דחיפת אימוץ (B3.1)",
    zh: "采用推送 (B3.1) 状态",
    it: "Stato adozione push (B3.1)",
    fr: "Statut de promotion d'adoption (B3.1)",
    de: "Status Adoption-Push (B3.1)",
    "pt-br": "Status do incentivo de adoção (B3.1)",
    ja: "採用促進（B3.1）ステータス",
    hi: "अपनाने की पुश (B3.1) स्थिति",
    ko: "도입 푸시(B3.1) 상태",
    ru: "Статус продвижения внедрения (B3.1)",
    cs: "Stav zavedení (B3.1)",
    nl: "Status adoptie-push (B3.1)",
  }, lang),
  readinessAdoptionRunning: pick({
    en: "Active · in-app prompt + Settings banner",
    es: "Activo · prompt in-app + banner en Settings",
    ca: "Actiu · avís a l'aplicació + bàner de Configuració",
    eu: "Aktibo · aplikazio barneko gonbita + Ezarpenen banner-a",
    gl: "Activo · aviso na aplicación + banner de Configuración",
    ar: "نشط · مطالبة داخل التطبيق + لافتة الإعدادات",
    he: "פעיל · בקשה באפליקציה + באנר בהגדרות",
    zh: "活跃 · 应用内提示 + 设置横幅",
    it: "Attiva · prompt in-app + banner Impostazioni",
    fr: "Active · invite in-app + bannière Paramètres",
    de: "Aktiv · In-App-Eingabeaufforderung + Einstellungs-Banner",
    "pt-br": "Ativo · prompt no app + banner em Configurações",
    ja: "アクティブ · アプリ内プロンプト + 設定バナー",
    hi: "सक्रिय · इन-ऐप प्रॉम्प्ट + सेटिंग बैनर",
    ko: "활성 · 인앱 프롬프트 + 설정 배너",
    ru: "Активно · внутреннее уведомление + баннер в настройках",
    cs: "Aktivní · výzva v aplikaci + banner v Nastavení",
    nl: "Actief · in-app prompt + banner in Instellingen",
  }, lang),
  // Next milestone footer — reads as roadmap, not as limitation.
  // The {current} placeholder is replaced at render time with
  // the snapshot's `total_users_included` value.
  milestoneLabel: pick({
    en: "Next milestone",
    es: "Próximo hito",
    ca: "Següent fita",
    eu: "Hurrengo mugarria",
    gl: "Seguinte fito",
    ar: "المعلم التالي",
    he: "אבן דרך הבאה",
    zh: "下一个里程碑",
    it: "Prossimo traguardo",
    fr: "Prochaine étape",
    de: "Nächster Meilenstein",
    "pt-br": "Próximo marco",
    ja: "次のマイルストーン",
    hi: "अगला माइलस्टोन",
    ko: "다음 이정표",
    ru: "Следующий этап",
    cs: "Další milník",
    nl: "Volgende mijlpaal",
  }, lang),
  milestoneBody: pick({
    en: "Phase 2 Sponsor View unlocks when at least 25 users complete Wishlist Preferences.",
    es: "Fase 2 Vista Sponsor se desbloquea cuando al menos 25 usuarios completen sus Preferencias de Wishlist.",
    ca: "La Fase 2 Vista de Patrocinador es desbloqueja quan almenys 25 usuaris completin les Preferències de Wishlist.",
    eu: "2. faseko Babeslearen Ikuspegia desblokeatzen da gutxienez 25 erabiltzailek Nahi-zerrenda Hobespenak osatzen dituztenean.",
    gl: "A Vista de Patrocinador da Fase 2 desbloquéase cando polo menos 25 usuarios completen as Preferencias da Lista de Desexos.",
    ar: "عرض الرعاة للمرحلة 2 يُفتح عندما يكمل 25 مستخدمًا على الأقل تفضيلات قائمة الرغبات.",
    he: "תצוגת ספונסר שלב 2 תיפתח כאשר לפחות 25 משתמשים ישלימו את העדפות רשימת המשאלות.",
    zh: "第二阶段赞助商视图将在至少 25 位用户完成心愿单偏好设置后解锁。",
    it: "La Vista sponsor Fase 2 si sblocca quando almeno 25 utenti completano le Preferenze Wishlist.",
    fr: "La Vue sponsor de Phase 2 se débloque quand au moins 25 utilisateurs ont complété leurs Préférences de Liste de Souhaits.",
    de: "Phase 2 Sponsor-Ansicht wird freigeschaltet, wenn mindestens 25 Benutzer Wunschlistenpräferenzen vervollständigt haben.",
    "pt-br": "A Visão do Patrocinador da Fase 2 é desbloqueada quando pelo menos 25 usuários completarem as Preferências da Lista de Desejos.",
    ja: "フェーズ2スポンサービューは、最低25名のユーザーがウィッシュリスト設定を完了するとアンロックされます。",
    hi: "फेज 2 प्रायोजक दृश्य अनलॉक होता है जब कम से कम 25 उपयोगकर्ता विशलिस्ट प्राथमिकताएँ पूर्ण करें।",
    ko: "최소 25명의 사용자가 위시리스트 환경설정을 완료하면 2단계 스폰서 보기가 잠금 해제됩니다.",
    ru: "Фаза 2 «Представление спонсора» открывается, когда минимум 25 пользователей завершат настройку предпочтений списка желаний.",
    cs: "Fáze 2 Pohled sponzora se odemkne, když alespoň 25 uživatelů dokončí Preference seznamu přání.",
    nl: "Fase 2 Sponsorweergave wordt ontgrendeld wanneer minstens 25 gebruikers Verlanglijstvoorkeuren hebben voltooid.",
  }, lang),
  milestoneCurrent: pick({
    en: "Current",
    es: "Actual",
    ca: "Actual",
    eu: "Oraingoa",
    gl: "Actual",
    ar: "الحالي",
    he: "נוכחי",
    zh: "当前",
    it: "Attuale",
    fr: "Actuel",
    de: "Aktuell",
    "pt-br": "Atual",
    ja: "現在",
    hi: "वर्तमान",
    ko: "현재",
    ru: "Текущий",
    cs: "Aktuální",
    nl: "Huidig",
  }, lang),
  milestoneTarget: pick({
    en: "Target",
    es: "Objetivo",
    ca: "Objectiu",
    eu: "Helburua",
    gl: "Obxectivo",
    ar: "الهدف",
    he: "יעד",
    zh: "目标",
    it: "Obiettivo",
    fr: "Cible",
    de: "Ziel",
    "pt-br": "Meta",
    ja: "目標",
    hi: "लक्ष्य",
    ko: "목표",
    ru: "Целевой",
    cs: "Cíl",
    nl: "Doel",
  }, lang),

  // ---------------------------------------------------------------
  // B3.3 Light — WLP Adoption admin mini-dashboard (Feb 2026).
  // Aggregate funnel only. K-anon enforced server-side. No PII.
  // ---------------------------------------------------------------
  adoptionTitle: pick({
    en: "WLP adoption funnel",
    es: "Funnel de adopción WLP",
    ca: "Embut d'adopció WLP",
    eu: "WLP adopzio inbutua",
    gl: "Funil de adopción WLP",
    ar: "مسار تبني WLP",
    he: "משפך אימוץ WLP",
    zh: "WLP 采用漏斗",
    it: "Funnel adozione WLP",
    fr: "Entonnoir d'adoption WLP",
    de: "WLP-Adoption-Funnel",
    "pt-br": "Funil de adoção WLP",
    ja: "WLP採用ファネル",
    hi: "WLP अपनाने का फ़नल",
    ko: "WLP 도입 퍼널",
    ru: "Воронка внедрения WLP",
    cs: "Nálevka zavedení WLP",
    nl: "WLP-adoptiefunnel",
  }, lang),
  adoptionSub: pick({
    en: "Aggregate, k-anonymous adoption signal for Wishlist Preferences. Gates Phase 2 Sponsor View readiness.",
    es: "Señal agregada y k-anónima de adopción de Preferencias de Wishlist. Habilita la preparación para la Fase 2 Vista Sponsor.",
    ca: "Senyal d'adopció agregat i k-anònim per a Preferències de Wishlist. Controla la preparació de la Vista de Patrocinador de la Fase 2.",
    eu: "Nahi-zerrenda Hobespenentzako seinale agregatu eta k-anonimoa. 2. faseko Babeslearen Ikuspegiaren prest egoera mugatzen du.",
    gl: "Sinal agregado e k-anónimo de adopción para Preferencias da Lista de Desexos. Condiciona a preparación da Vista de Patrocinador da Fase 2.",
    ar: "إشارة تبني مجمعة ومجهولة الهوية لتفضيلات قائمة الرغبات. تحكم جاهزية عرض الرعاة للمرحلة 2.",
    he: "אות אימוץ מצטבר ואנונימי-k עבור העדפות רשימת המשאלות. שומר על מוכנות תצוגת ספונסר שלב 2.",
    zh: "心愿单偏好设置的聚合、k-匿名采用信号。控制第二阶段赞助商视图就绪状态。",
    it: "Segnale di adozione aggregato e k-anonimo per Preferenze Wishlist. Determina la disponibilità della Vista sponsor Fase 2.",
    fr: "Signal d'adoption agrégé, k-anonyme pour Préférences de Liste de Souhaits. Conditionne l'accès à la Vue sponsor Phase 2.",
    de: "Aggregiertes, k-anonymes Adoptionssignal für Wunschlistenpräferenzen. Steuert Bereitschaft für Phase 2 Sponsor-Ansicht.",
    "pt-br": "Sinal de adoção agregado e k-anônimo para Preferências da Lista de Desejos. Controla a preparação da Visão do Patrocinador da Fase 2.",
    ja: "ウィッシュリスト設定の集約されたk匿名化採用シグナル。フェーズ2スポンサービューの準備状況をゲートします。",
    hi: "विशलिस्ट प्राथमिकताओं के लिए समग्र, k-अनाम अपनाने का संकेत। फेज 2 प्रायोजक दृश्य तत्परता को गेट करता है।",
    ko: "위시리스트 환경설정에 대한 집계된 k-익명 도입 신호입니다. 2단계 스폰서 보기 준비 상태를 관리합니다.",
    ru: "Агрегированный, k-анонимный сигнал внедрения для предпочтений списка желаний. Контролирует готовность фазы 2 «Представление спонсора».",
    cs: "Agregovaný, k-anonymní signál zavedení pro Preference seznamu přání. Kontroluje připravenost Fáze 2 Pohled sponzora.",
    nl: "Geaggregeerd, k-anoniem adoptiesignaal voor Verlanglijstvoorkeuren. Bepaalt gereedheid Fase 2 Sponsorweergave.",
  }, lang),
  adoptionRefresh: pick({
    en: "Refresh",
    es: "Recargar",
    ca: "Actualitza",
    eu: "Freskatu",
    gl: "Actualizar",
    ar: "تحديث",
    he: "רענן",
    zh: "刷新",
    it: "Aggiorna",
    fr: "Actualiser",
    de: "Aktualisieren",
    "pt-br": "Atualizar",
    ja: "更新",
    hi: "रीफ़्रेश करें",
    ko: "새로고침",
    ru: "Обновить",
    cs: "Obnovit",
    nl: "Vernieuwen",
  }, lang),
  adoptionMetricEligible: pick({
    en: "Eligible cohort",
    es: "Cohorte elegible",
    ca: "Cohort elegible",
    eu: "Kohorte balioduna",
    gl: "Cohorte elixible",
    ar: "المجموعة المؤهلة",
    he: "קבוצת יעד זכאית",
    zh: "符合条件的群体",
    it: "Coorte idonea",
    fr: "Cohorte éligible",
    de: "Berechtigte Kohorte",
    "pt-br": "Coorte elegível",
    ja: "対象コホート",
    hi: "योग्य समूह",
    ko: "적격 코호트",
    ru: "Целевая когорта",
    cs: "Způsobilá kohorta",
    nl: "In aanmerking komend cohort",
  }, lang),
  adoptionMetricEligibleHint: pick({
    en: "Verified, non-internal, non-minor users.",
    es: "Usuarios verificados, no internos y no menores.",
    ca: "Usuaris verificats, no interns, no menors.",
    eu: "Egiaztatutako, ez-barne, ez-adingabe erabiltzaileak.",
    gl: "Usuarios verificados, non internos, non menores.",
    ar: "مستخدمون موثقون، غير داخليين، غير قاصرين.",
    he: "משתמשים מאומתים, לא פנימיים, לא קטינים.",
    zh: "已验证、非内部、非未成年用户。",
    it: "Utenti verificati, non interni, non minorenni.",
    fr: "Utilisateurs vérifiés, non-internes, non-mineurs.",
    de: "Verifizierte, nicht-interne, nicht-minderjährige Benutzer.",
    "pt-br": "Usuários verificados, não internos e não menores de idade.",
    ja: "検証済み、非内部、非未成年ユーザー。",
    hi: "सत्यापित, गैर-आंतरिक, गैर-नाबालिग उपयोगकर्ता।",
    ko: "인증되고 내부가 아니며 미성년자가 아닌 사용자",
    ru: "Верифицированные, не внутренние, совершеннолетние пользователи.",
    cs: "Ověření, neinterní uživatelé bez nezletilých.",
    nl: "Geverifieerde, niet-interne, niet-minderjarige gebruikers.",
  }, lang),
  adoptionMetricWithWlp: pick({
    en: "With useful WLP",
    es: "Con WLP útil",
    ca: "Amb WLP útil",
    eu: "WLP erabilgarriarekin",
    gl: "Con WLP útil",
    ar: "مع WLP مفيدة",
    he: "עם WLP שימושי",
    zh: "有效 WLP",
    it: "Con WLP utile",
    fr: "Avec WLP utile",
    de: "Mit nützlicher WLP",
    "pt-br": "Com WLP útil",
    ja: "有効なWLP保有",
    hi: "उपयोगी WLP के साथ",
    ko: "유용한 WLP 보유",
    ru: "С полезными WLP",
    cs: "S užitečnými WLP",
    nl: "Met bruikbare WLP",
  }, lang),
  adoptionMetricWithWlpHint: pick({
    en: "Brands, categories or sponsorship signals set.",
    es: "Marcas, categorías o señales de patrocinio configuradas.",
    ca: "Marques, categories o senyals de patrocini establerts.",
    eu: "Markak, kategoriak edo babesle seinaleak ezarrita.",
    gl: "Marcas, categorías ou sinais de patrocinio establecidos.",
    ar: "العلامات التجارية أو الفئات أو إشارات الرعاية محددة.",
    he: "מותגים, קטגוריות או אותות חסות מוגדרים.",
    zh: "已设置品牌、类别或赞助信号。",
    it: "Brand, categorie o segnali di sponsorizzazione impostati.",
    fr: "Marques, catégories ou signaux de sponsoring définis.",
    de: "Marken, Kategorien oder Sponsoring-Signale festgelegt.",
    "pt-br": "Marcas, categorias ou sinais de patrocínio definidos.",
    ja: "ブランド、カテゴリー、スポンサーシップシグナルが設定済み。",
    hi: "ब्रांड, श्रेणियाँ या प्रायोजन संकेत सेट किए गए।",
    ko: "브랜드, 카테고리 또는 스폰서십 신호가 설정됨",
    ru: "Заданы бренды, категории или сигналы спонсорства.",
    cs: "Značky, kategorie nebo signály sponzorství nastaveny.",
    nl: "Merken, categorieën of sponsorsignalen ingesteld.",
  }, lang),
  adoptionMetricRate: pick({
    en: "Adoption rate",
    es: "Tasa de adopción",
    ca: "Taxa d'adopció",
    eu: "Adopzio tasa",
    gl: "Taxa de adopción",
    ar: "معدل التبني",
    he: "שיעור אימוץ",
    zh: "采用率",
    it: "Tasso di adozione",
    fr: "Taux d'adoption",
    de: "Adoptionsrate",
    "pt-br": "Taxa de adoção",
    ja: "採用率",
    hi: "अपनाने की दर",
    ko: "도입률",
    ru: "Уровень внедрения",
    cs: "Míra zavedení",
    nl: "Adoptiepercentage",
  }, lang),
  adoptionMetricImpressions: pick({
    en: "Impressions",
    es: "Impresiones",
    ca: "Impressions",
    eu: "Inpresioak",
    gl: "Impresións",
    ar: "مرات الظهور",
    he: "חשיפות",
    zh: "展示次数",
    it: "Impressioni",
    fr: "Impressions",
    de: "Impressionen",
    "pt-br": "Impressões",
    ja: "インプレッション",
    hi: "इंप्रेशन",
    ko: "노출 수",
    ru: "Показы",
    cs: "Zobrazení",
    nl: "Vertoningen",
  }, lang),
  adoptionMetricClicks: pick({
    en: "Clicks",
    es: "Clics",
    ca: "Clics",
    eu: "Klikak",
    gl: "Clics",
    ar: "النقرات",
    he: "לחיצות",
    zh: "点击次数",
    it: "Clic",
    fr: "Clics",
    de: "Klicks",
    "pt-br": "Cliques",
    ja: "クリック",
    hi: "क्लिक",
    ko: "클릭 수",
    ru: "Клики",
    cs: "Kliknutí",
    nl: "Klikken",
  }, lang),
  adoptionMetricDismissed: pick({
    en: "Dismissed",
    es: "Descartados",
    ca: "Descartat",
    eu: "Baztertuta",
    gl: "Rexeitado",
    ar: "تم التجاهل",
    he: "נדחה",
    zh: "已关闭",
    it: "Ignorate",
    fr: "Ignoré",
    de: "Abgelehnt",
    "pt-br": "Dispensado",
    ja: "却下済み",
    hi: "खारिज किए गए",
    ko: "무시됨",
    ru: "Отклонено",
    cs: "Zamítnuto",
    nl: "Gesloten",
  }, lang),
  adoptionMetricCompleted: pick({
    en: "Completed",
    es: "Completados",
    ca: "Completat",
    eu: "Osatuta",
    gl: "Completado",
    ar: "مكتمل",
    he: "הושלם",
    zh: "已完成",
    it: "Completate",
    fr: "Terminé",
    de: "Abgeschlossen",
    "pt-br": "Concluído",
    ja: "完了",
    hi: "पूर्ण",
    ko: "완료됨",
    ru: "Завершено",
    cs: "Dokončeno",
    nl: "Voltooid",
  }, lang),
  adoptionProgressLabel: pick({
    en: "Progress to Phase 2 unlock (25 users with useful WLP)",
    es: "Progreso hacia desbloqueo Phase 2 (25 usuarios con WLP útil)",
    ca: "Progrés per desbloquejar la Fase 2 (25 usuaris amb WLP útil)",
    eu: "2. fasera iristeko aurrerapena (WLP erabilgarria duten 25 erabiltzaile)",
    gl: "Progreso para desbloquear a Fase 2 (25 usuarios con WLP útil)",
    ar: "التقدم نحو فتح المرحلة 2 (25 مستخدمًا بسجل عمل مفيد)",
    he: "התקדמות לפתיחת שלב 2 (25 משתמשים עם WLP שימושי)",
    zh: "第2阶段解锁进度（25名用户拥有有效WLP）",
    it: "Progresso verso lo sblocco della Fase 2 (25 utenti con WLP utile)",
    fr: "Progression vers le déblocage de la Phase 2 (25 utilisateurs avec WLP utile)",
    de: "Fortschritt zur Freischaltung von Phase 2 (25 Nutzer mit nützlichem WLP)",
    "pt-br": "Progresso para desbloqueio da Fase 2 (25 usuários com WLP útil)",
    ja: "フェーズ2解除への進捗（有用なWLPを持つ25ユーザー）",
    hi: "फ़ेज़ 2 अनलॉक की प्रगति (उपयोगी WLP वाले 25 उपयोगकर्ता)",
    ko: "2단계 잠금 해제 진행률 (유용한 WLP를 가진 사용자 25명)",
    ru: "Прогресс для разблокировки Фазы 2 (25 пользователей с полезным WLP)",
    cs: "Postup k odemčení Fáze 2 (25 uživatelů s užitečným WLP)",
    nl: "Voortgang naar Fase 2 ontgrendeling (25 gebruikers met bruikbare WLP)",
  }, lang),
  adoptionStatusInsufficient: pick({
    en: "Insufficient signal",
    es: "Señal insuficiente",
    ca: "Senyal insuficient",
    eu: "Seinalea ez da nahikoa",
    gl: "Sinal insuficiente",
    ar: "إشارة غير كافية",
    he: "אין מספיק נתונים",
    zh: "信号不足",
    it: "Segnale insufficiente",
    fr: "Signal insuffisant",
    de: "Unzureichendes Signal",
    "pt-br": "Sinal insuficiente",
    ja: "シグナル不足",
    hi: "अपर्याप्त संकेत",
    ko: "신호 부족",
    ru: "Недостаточно данных",
    cs: "Nedostatečný signál",
    nl: "Onvoldoende signaal",
  }, lang),
  adoptionStatusInsufficientBody: pick({
    en: "Cohort below k-anonymity floor or no useful WLP yet. Keep nurturing adoption.",
    es: "Cohorte por debajo del umbral k-anónimo o sin WLP útil aún. Continúa impulsando la adopción.",
    ca: "Cohort per sota del llindar k-anonimat o encara sense WLP útil. Continua fomentant l'adopció.",
    eu: "Kohortea k-anonimotasun minimoaren azpitik dago edo oraindik ez dago WLP erabilgarririk. Jarraitu adopzioa bultzatzen.",
    gl: "Cohorte por debaixo do limiar de k-anonimato ou aínda non hai WLP útil. Continúa fomentando a adopción.",
    ar: "المجموعة أقل من حد عدم الكشف عن الهوية أو لا يوجد سجل عمل مفيد بعد. واصل تعزيز التبني.",
    he: "הקבוצה מתחת לרף k-anonymity או שאין עדיין WLP שימושי. המשיכו לטפח את האימוץ.",
    zh: "群组低于k-匿名性阈值或尚无有效WLP。请继续培养采用度。",
    it: "Coorte sotto la soglia di k-anonimato o nessun WLP utile ancora. Continua a promuovere l'adozione.",
    fr: "Cohorte en dessous du seuil k-anonymat ou pas encore de WLP utile. Continuez à encourager l'adoption.",
    de: "Kohorte unter k-Anonymitätsschwelle oder noch kein nützliches WLP. Fördern Sie weiter die Akzeptanz.",
    "pt-br": "Coorte abaixo do limite de k-anonimato ou ainda sem WLP útil. Continue incentivando a adoção.",
    ja: "コホートがk匿名性の下限を下回っているか、有用なWLPがまだありません。採用の育成を続けてください。",
    hi: "समूह k-anonymity सीमा से नीचे है या अभी तक कोई उपयोगी WLP नहीं। अपनाने को बढ़ावा देते रहें।",
    ko: "코호트가 k-익명성 최소값 미만이거나 아직 유용한 WLP가 없습니다. 도입을 계속 육성하세요.",
    ru: "Когорта ниже порога k-анонимности или пока нет полезного WLP. Продолжайте развивать вовлечённость.",
    cs: "Kohorta pod hranicí k-anonymity nebo zatím žádné užitečné WLP. Pokračujte v podpoře adopce.",
    nl: "Cohort onder k-anonimiteitsdrempel of nog geen bruikbare WLP. Blijf adoptie stimuleren.",
  }, lang),
  adoptionStatusApproaching: pick({
    en: "Approaching sponsor readiness",
    es: "Aproximándose a la preparación para sponsors",
    ca: "Aproximant-se a la preparació de patrocinador",
    eu: "Babeslearen prestatze-puntuan",
    gl: "Aproximándose á preparación do patrocinador",
    ar: "يقترب من جاهزية الراعي",
    he: "מתקרב לוויזת ספונסר",
    zh: "接近赞助商就绪状态",
    it: "Prossimo alla disponibilità sponsor",
    fr: "Prêt pour la revue sponsor bientôt",
    de: "Sponsor-Bereitschaft wird erreicht",
    "pt-br": "Aproximando-se da prontidão do patrocinador",
    ja: "スポンサー準備に近づいています",
    hi: "प्रायोजक तत्परता के निकट",
    ko: "스폰서 준비 근접",
    ru: "Приближается готовность спонсора",
    cs: "Blíží se připravenost sponzora",
    nl: "Sponsor-gereedheid nadert",
  }, lang),
  adoptionStatusApproachingBody: pick({
    en: "Adoption is climbing. Phase 2 Sponsor View is within reach — keep the in-app prompt running.",
    es: "La adopción crece. La Fase 2 Vista Sponsor está al alcance — mantén el prompt in-app activo.",
    ca: "L'adopció està creixent. La Vista de Patrocinador de la Fase 2 està a l'abast — mantén l'avís dins l'aplicació actiu.",
    eu: "Adopzioa gora doa. 2. faseko Babeslearen Ikuspegia gertu dago — mantendu aplikazio barruko mezua aktibo.",
    gl: "A adopción está a medrar. A Vista de Patrocinador da Fase 2 está ao alcance — mantén a aviso na aplicación activo.",
    ar: "التبني في ارتفاع. عرض الراعي للمرحلة 2 في متناول اليد — أبقِ التنبيه داخل التطبيق قيد التشغيل.",
    he: "האימוץ עולה. תצוגת הספונסר של שלב 2 בהישג יד — שמרו על ההודעה באפליקציה פעילה.",
    zh: "采用度正在提升。第2阶段赞助商视图触手可及——请保持应用内提示运行。",
    it: "L'adozione sta crescendo. La Vista Sponsor della Fase 2 è a portata di mano — mantieni attivo il prompt in-app.",
    fr: "L'adoption progresse. La Vue Sponsor de la Phase 2 est à portée — maintenez l'invite dans l'application.",
    de: "Akzeptanz steigt. Phase 2 Sponsor View ist in Reichweite – lassen Sie die In-App-Aufforderung weiterlaufen.",
    "pt-br": "A adoção está crescendo. A Visualização de Patrocinador da Fase 2 está ao alcance — mantenha o prompt no aplicativo ativo.",
    ja: "採用が増加しています。フェーズ2スポンサービューが手の届くところにあります — アプリ内プロンプトを継続してください。",
    hi: "अपनाना बढ़ रहा है। फ़ेज़ 2 प्रायोजक व्यू पहुँच में है — इन-ऐप प्रॉम्प्ट चालू रखें।",
    ko: "도입률이 상승하고 있습니다. 2단계 스폰서 뷰가 곧 도달 가능합니다 — 인앱 프롬프트를 계속 실행하세요.",
    ru: "Вовлечённость растёт. Спонсорский вид Фазы 2 в пределах досягаемости — оставьте подсказку в приложении активной.",
    cs: "Adopce roste. Zobrazení Sponzora ve Fázi 2 je na dosah — nechte v aplikaci běžet výzvu.",
    nl: "Adoptie neemt toe. Fase 2 Sponsor View komt in zicht — laat de in-app prompt actief.",
  }, lang),
  adoptionStatusReady: pick({
    en: "Ready for Phase 2 review",
    es: "Listo para revisión de Phase 2",
    ca: "Llest per a la revisió de Fase 2",
    eu: "2. faseko berrikuspenerako prest",
    gl: "Preparado para a revisión da Fase 2",
    ar: "جاهز لمراجعة المرحلة 2",
    he: "מוכן לסקירת שלב 2",
    zh: "准备进行第2阶段审核",
    it: "Pronto per la revisione Fase 2",
    fr: "Prêt pour la revue Phase 2",
    de: "Bereit für Phase-2-Prüfung",
    "pt-br": "Pronto para revisão da Fase 2",
    ja: "フェーズ2レビューの準備完了",
    hi: "फ़ेज़ 2 समीक्षा के लिए तैयार",
    ko: "2단계 검토 준비 완료",
    ru: "Готово к проверке Фазы 2",
    cs: "Připraveno k revizi Fáze 2",
    nl: "Klaar voor Fase 2 beoordeling",
  }, lang),
  adoptionStatusReadyBody: pick({
    en: "Threshold met. Schedule the Phase 2 readiness review and the Sponsor View rollout plan.",
    es: "Umbral alcanzado. Programa la revisión de Phase 2 y el plan de despliegue de Sponsor View.",
    ca: "Llindar assolit. Programa la revisió de preparació de Fase 2 i el pla de desplegament de la Vista de Patrocinador.",
    eu: "Atalasea lortu da. Programatu 2. faseko prestatze-berrikuspena eta Babeslearen Ikuspegiaren zabalpen-plana.",
    gl: "Limiar alcanzado. Programa a revisión de preparación da Fase 2 e o plan de despregamento da Vista de Patrocinador.",
    ar: "تم الوصول إلى الحد الأدنى. جدول مراجعة جاهزية المرحلة 2 وخطة طرح عرض الراعي.",
    he: "הסף הושג. תזמנו את סקירת מוכנות שלב 2 ואת תכנית הפריסה של תצוגת הספונסר.",
    zh: "已达到阈值。请安排第2阶段就绪审核和赞助商视图推出计划。",
    it: "Soglia raggiunta. Pianifica la revisione di disponibilità Fase 2 e il piano di rollout Vista Sponsor.",
    fr: "Seuil atteint. Planifiez la revue de préparation Phase 2 et le plan de déploiement de la Vue Sponsor.",
    de: "Schwellenwert erreicht. Planen Sie die Phase-2-Bereitschaftsprüfung und den Rollout-Plan für Sponsor View.",
    "pt-br": "Limite atingido. Agende a revisão de prontidão da Fase 2 e o plano de implementação da Visualização de Patrocinador.",
    ja: "閾値に到達しました。フェーズ2準備レビューとスポンサービューのロールアウト計画をスケジュールしてください。",
    hi: "सीमा पूर्ण। फ़ेज़ 2 तत्परता समीक्षा और प्रायोजक व्यू रोलआउट योजना निर्धारित करें।",
    ko: "기준 충족. 2단계 준비 검토 및 스폰서 뷰 출시 계획을 예약하세요.",
    ru: "Порог достигнут. Запланируйте проверку готовности Фазы 2 и план внедрения Спонсорского вида.",
    cs: "Práh splněn. Naplánujte revizi připravenosti Fáze 2 a plán zavedení Zobrazení Sponzora.",
    nl: "Drempelwaarde bereikt. Plan de Fase 2 gereedheidsbeoordeling en het Sponsor View uitrolplan.",
  }, lang),
  adoptionKAnonNote: pick({
    en: "K-anonymity floor enforced server-side. No user IDs, no emails, no names rendered here.",
    es: "Umbral k-anónimo aplicado en el servidor. No se renderizan IDs, emails ni nombres aquí.",
    ca: "Llindar k-anonimat aplicat al servidor. Cap ID d'usuari, correu electrònic o nom es mostra aquí.",
    eu: "K-anonimotasun minimoa zerbitzarian aplikatuta. Ez da erabiltzaile-IDrik, emailik edo izenik erakusten hemen.",
    gl: "Limiar de k-anonimato aplicado no servidor. Non se mostran IDs de usuario, correos electrónicos nin nomes aquí.",
    ar: "يتم فرض حد عدم الكشف عن الهوية من جانب الخادم. لا معرفات مستخدمين، لا رسائل بريد إلكتروني، لا أسماء معروضة هنا.",
    he: "רף k-anonymity נאכף בצד השרת. ללא מזהי משתמש, אימיילים או שמות המוצגים כאן.",
    zh: "服务器端强制执行K-匿名性阈值。此处不呈现用户ID、电子邮件或姓名。",
    it: "Soglia di k-anonimato applicata lato server. Nessun ID utente, email o nome visualizzato qui.",
    fr: "Seuil k-anonymat appliqué côté serveur. Aucun ID utilisateur, aucun email, aucun nom affiché ici.",
    de: "K-Anonymitätsschwelle serverseitig durchgesetzt. Keine Nutzer-IDs, keine E-Mails, keine Namen hier angezeigt.",
    "pt-br": "Limite de k-anonimato aplicado no servidor. Nenhum ID de usuário, e-mail ou nome renderizado aqui.",
    ja: "k匿名性の下限はサーバー側で適用されます。ユーザーID、メール、名前はここに表示されません。",
    hi: "K-anonymity सीमा सर्वर-साइड लागू। यहाँ कोई उपयोगकर्ता ID, ईमेल या नाम प्रदर्शित नहीं।",
    ko: "K-익명성 최소값이 서버 측에서 적용됩니다. 사용자 ID, 이메일, 이름은 여기에 표시되지 않습니다.",
    ru: "Порог k-анонимности соблюдается на стороне сервера. ID пользователей, email-адреса, имена здесь не отображаются.",
    cs: "Hranice k-anonymity vynucena na straně serveru. Žádná ID uživatelů, e-maily ani jména zde nejsou zobrazeny.",
    nl: "K-anonimiteitsdrempel server-side afgedwongen. Geen gebruikers-ID's, geen e-mails, geen namen weergegeven.",
  }, lang),
  adoptionLoadError: pick({
    en: "Could not load adoption metrics.",
    es: "No se pudieron cargar las métricas de adopción.",
    ca: "No s'han pogut carregar les mètriques d'adopció.",
    eu: "Ezin izan dira adopzio-metrikak kargatu.",
    gl: "Non se puideron cargar as métricas de adopción.",
    ar: "تعذر تحميل مقاييس التبني.",
    he: "לא ניתן לטעון נתוני אימוץ.",
    zh: "无法加载采用度指标。",
    it: "Impossibile caricare le metriche di adozione.",
    fr: "Impossible de charger les métriques d'adoption.",
    de: "Akzeptanzmetriken konnten nicht geladen werden.",
    "pt-br": "Não foi possível carregar as métricas de adoção.",
    ja: "採用指標を読み込めませんでした。",
    hi: "अपनाने के मेट्रिक्स लोड नहीं हो सके।",
    ko: "도입 지표를 로드할 수 없습니다.",
    ru: "Не удалось загрузить метрики вовлечённости.",
    cs: "Nelze načíst metriky adopce.",
    nl: "Kon adoptiemetrieken niet laden.",
  }, lang),
  adoptionEmptyValue: pick({
    en: "—",
    es: "—",
    ca: "—",
    eu: "—",
    gl: "—",
    ar: "—",
    he: "—",
    zh: "—",
    it: "—",
    fr: "—",
    de: "—",
    "pt-br": "—",
    ja: "—",
    hi: "—",
    ko: "—",
    ru: "—",
    cs: "—",
    nl: "—",
  }, lang),
});

const LANGS = ["en", "es", "zh", "it", "fr", "de", "pt-br", "ja", "hi", "ko", "ru", "cs", "nl", "ca", "eu", "gl", "ar", "he"];

export const MARKETPLACE_DEMAND_I18N = Object.fromEntries(LANGS.map((l) => [l, _DEMAND(l)]));
